import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Tooltip,
  IconButton,
  TextField,
  Button,
  ButtonGroup,
  useTheme,
  Link,
  Stack,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
  YouTube as YouTubeIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";
import HeightIcon from "@mui/icons-material/Height";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import StraightenIcon from "@mui/icons-material/Straighten";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
// Removed duplicate imports

const AthleteVitals = ({
  athlete,
  onUpdateAthlete,
  onNavigateToNotes,
  currentUser,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);

  // Initialize editedAthlete with all necessary nested properties
  const [editedAthlete, setEditedAthlete] = useState(() => {
    // Ensure all required nested objects exist
    return {
      ...athlete,
      standingReach: athlete.standingReach || null,
      benchPress: athlete.benchPress || null,
      dateOfBirth: athlete.dateOfBirth || null,
      verification: athlete.verification || {},
      academics: athlete.academics || {
        gpa: athlete.academics?.gpa || null,
        transcripts: athlete.academics?.transcripts || [],
        coreGpa: athlete.academics?.coreGpa || null,
      },
      address: athlete.address || {
        street: athlete.address?.street || null,
        city: athlete.address?.city || null,
        state: athlete.address?.state || null,
        zipCode: athlete.address?.zipCode || null,
        country: athlete.address?.country || "USA",
      },
      socialMedia: athlete.socialMedia || {
        twitter: athlete.socialMedia?.twitter || null,
        instagram: athlete.socialMedia?.instagram || null,
        hudl: Array.isArray(athlete.socialMedia?.hudl)
          ? athlete.socialMedia.hudl
          : athlete.socialMedia?.hudl
          ? [athlete.socialMedia.hudl]
          : [],
        youtube: Array.isArray(athlete.socialMedia?.youtube)
          ? athlete.socialMedia.youtube
          : athlete.socialMedia?.youtube
          ? [athlete.socialMedia.youtube]
          : [],
      },
      speedData: athlete.speedData || {
        fortyYard:
          athlete.speedData?.fortyYard || athlete.fortyYardDash || null,
        shuttleRun: athlete.speedData?.shuttleRun || athlete.shuttleRun || null,
        sixtyYardDash: athlete.speedData?.sixtyYardDash || null,
        hundredMeter: athlete.speedData?.hundredMeter || null,
      },
      evaluations: Array.isArray(athlete.evaluations)
        ? athlete.evaluations
        : athlete.evaluations
        ? [
            {
              text: athlete.evaluations,
              date: new Date().toISOString(),
              author:
                currentUser?.firstName && currentUser?.lastName
                  ? `${currentUser.firstName} ${currentUser.lastName}`
                  : currentUser?.name || "Coach",
            },
          ]
        : [],
      injuryHistory: Array.isArray(athlete.injuryHistory)
        ? athlete.injuryHistory
        : athlete.injuryHistory
        ? [
            {
              text: athlete.injuryHistory,
              date: new Date().toISOString(),
              author:
                currentUser?.firstName && currentUser?.lastName
                  ? `${currentUser.firstName} ${currentUser.lastName}`
                  : currentUser?.name || "Coach",
            },
          ]
        : [],
    };
  });

  // Format date
  const formatDate = (dateString) => {
    try {
      if (!dateString) {
        return null;
      }

      // Handle different date formats
      let date;
      if (typeof dateString === "string") {
        // Remove timezone info if present and try to parse
        const cleanDateString = dateString.split("T")[0]; // Remove time part
        date = new Date(cleanDateString);
      } else if (dateString instanceof Date) {
        date = dateString;
      } else {
        date = new Date(dateString);
      }

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.error("Invalid date:", dateString, "Parsed as:", date);
        return null;
      }

      const options = { year: "numeric", month: "short", day: "numeric" };
      return date.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error(
        "Error formatting date:",
        error,
        "Date string:",
        dateString
      );
      return null;
    }
  };

  // Calculate percentile indicator color
  const getPercentileColor = (percentile) => {
    if (percentile >= 90) return theme.palette.success.main;
    if (percentile >= 70) return theme.palette.success.light;
    if (percentile >= 50) return theme.palette.warning.main;
    if (percentile >= 30) return theme.palette.warning.light;
    return theme.palette.error.light;
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset to original values with all nested properties
      setEditedAthlete({
        ...athlete,
        standingReach: athlete.standingReach || null,
        benchPress: athlete.benchPress || null,
        academics: athlete.academics || {
          gpa: athlete.academics?.gpa || null,
          transcripts: athlete.academics?.transcripts || [],
          coreGpa: athlete.academics?.coreGpa || null,
        },
        address: athlete.address || {
          street: athlete.address?.street || null,
          city: athlete.address?.city || null,
          state: athlete.address?.state || null,
          zipCode: athlete.address?.zipCode || null,
          country: athlete.address?.country || "USA",
        },
        socialMedia: athlete.socialMedia || {
          twitter: athlete.socialMedia?.twitter || null,
          instagram: athlete.socialMedia?.instagram || null,
          hudl: Array.isArray(athlete.socialMedia?.hudl)
            ? athlete.socialMedia.hudl
            : athlete.socialMedia?.hudl
            ? [athlete.socialMedia.hudl]
            : [],
          youtube: Array.isArray(athlete.socialMedia?.youtube)
            ? athlete.socialMedia.youtube
            : athlete.socialMedia?.youtube
            ? [athlete.socialMedia.youtube]
            : [],
        },
        speedData: athlete.speedData || {
          fortyYard:
            athlete.speedData?.fortyYard || athlete.fortyYardDash || null,
          shuttleRun:
            athlete.speedData?.shuttleRun || athlete.shuttleRun || null,
          sixtyYardDash: athlete.speedData?.sixtyYardDash || null,
          hundredMeter: athlete.speedData?.hundredMeter || null,
        },
        evaluations: Array.isArray(athlete.evaluations)
          ? athlete.evaluations
          : athlete.evaluations
          ? [
              {
                text: athlete.evaluations,
                date: new Date().toISOString(),
                author:
                  currentUser?.firstName && currentUser?.lastName
                    ? `${currentUser.firstName} ${currentUser.lastName}`
                    : currentUser?.name || "Coach",
              },
            ]
          : [],
        injuryHistory: Array.isArray(athlete.injuryHistory)
          ? athlete.injuryHistory
          : athlete.injuryHistory
          ? [
              {
                text: athlete.injuryHistory,
                date: new Date().toISOString(),
                author:
                  currentUser?.firstName && currentUser?.lastName
                    ? `${currentUser.firstName} ${currentUser.lastName}`
                    : currentUser?.name || "Coach",
              },
            ]
          : [],
        verification: athlete.verification || {},
        dateOfBirth: athlete.dateOfBirth || null,
      });
    }
    setIsEditing(!isEditing);
  };

  // Handle save changes
  const handleSave = () => {
    if (onUpdateAthlete) {
      // Clean the data before sending to backend
      const cleanedAthlete = { ...editedAthlete };

      // Clean academics data - remove "N/A" and convert empty strings to null
      if (cleanedAthlete.academics) {
        // Handle GPA field
        if (
          cleanedAthlete.academics.gpa === "N/A" ||
          cleanedAthlete.academics.gpa === "" ||
          cleanedAthlete.academics.gpa === null
        ) {
          cleanedAthlete.academics.gpa = null;
        } else if (
          cleanedAthlete.academics.gpa &&
          !isNaN(parseFloat(cleanedAthlete.academics.gpa))
        ) {
          cleanedAthlete.academics.gpa = parseFloat(
            cleanedAthlete.academics.gpa
          );
        } else {
          // If GPA is not a valid number, set to null
          cleanedAthlete.academics.gpa = null;
        }

        // Handle coreGPA field
        if (
          cleanedAthlete.academics.coreGpa === "N/A" ||
          cleanedAthlete.academics.coreGpa === "" ||
          cleanedAthlete.academics.coreGpa === null
        ) {
          cleanedAthlete.academics.coreGpa = null;
        } else if (
          cleanedAthlete.academics.coreGpa &&
          !isNaN(parseFloat(cleanedAthlete.academics.coreGpa))
        ) {
          cleanedAthlete.academics.coreGpa = parseFloat(
            cleanedAthlete.academics.coreGpa
          );
        } else {
          // If coreGPA is not a valid number, set to null
          cleanedAthlete.academics.coreGpa = null;
        }
      }

      // Clean numeric fields - remove "N/A" and convert empty strings to null, convert to numbers
      // Handle height and weight directly
      if (
        cleanedAthlete.height === "N/A" ||
        cleanedAthlete.height === "" ||
        cleanedAthlete.height === null
      ) {
        cleanedAthlete.height = null;
      }
      // Height stays as string in the model

      if (
        cleanedAthlete.weight === "N/A" ||
        cleanedAthlete.weight === "" ||
        cleanedAthlete.weight === null
      ) {
        cleanedAthlete.weight = null;
      } else if (
        cleanedAthlete.weight &&
        !isNaN(parseFloat(cleanedAthlete.weight))
      ) {
        cleanedAthlete.weight = parseFloat(cleanedAthlete.weight);
      } else {
        cleanedAthlete.weight = null;
      }

      // Handle wingspan
      if (
        cleanedAthlete.wingspan === "N/A" ||
        cleanedAthlete.wingspan === "" ||
        cleanedAthlete.wingspan === null
      ) {
        cleanedAthlete.wingspan = null;
      } else if (
        cleanedAthlete.wingspan &&
        !isNaN(parseFloat(cleanedAthlete.wingspan))
      ) {
        cleanedAthlete.wingspan = parseFloat(cleanedAthlete.wingspan);
      } else {
        cleanedAthlete.wingspan = null;
      }

      // Handle vertical jump
      if (
        cleanedAthlete.verticalJump === "N/A" ||
        cleanedAthlete.verticalJump === "" ||
        cleanedAthlete.verticalJump === null
      ) {
        cleanedAthlete.verticalJump = null;
      } else if (
        cleanedAthlete.verticalJump &&
        !isNaN(parseFloat(cleanedAthlete.verticalJump))
      ) {
        cleanedAthlete.verticalJump = parseFloat(cleanedAthlete.verticalJump);
      } else {
        cleanedAthlete.verticalJump = null;
      }

      // Initialize speedData if it doesn't exist
      if (!cleanedAthlete.speedData) {
        cleanedAthlete.speedData = {};
      }

      // Debug logging
      console.log("SpeedData before cleaning:", cleanedAthlete.speedData);

      // Handle speedData.fortyYard - clean the nested field
      if (
        cleanedAthlete.speedData &&
        cleanedAthlete.speedData.fortyYard !== undefined
      ) {
        if (
          cleanedAthlete.speedData.fortyYard === "N/A" ||
          cleanedAthlete.speedData.fortyYard === "" ||
          cleanedAthlete.speedData.fortyYard === null
        ) {
          cleanedAthlete.speedData.fortyYard = null;
        } else if (
          cleanedAthlete.speedData.fortyYard &&
          !isNaN(parseFloat(cleanedAthlete.speedData.fortyYard))
        ) {
          cleanedAthlete.speedData.fortyYard = parseFloat(
            cleanedAthlete.speedData.fortyYard
          );
        } else {
          cleanedAthlete.speedData.fortyYard = null;
        }
      }

      // Handle speedData.shuttleRun - clean the nested field
      if (
        cleanedAthlete.speedData &&
        cleanedAthlete.speedData.shuttleRun !== undefined
      ) {
        if (
          cleanedAthlete.speedData.shuttleRun === "N/A" ||
          cleanedAthlete.speedData.shuttleRun === "" ||
          cleanedAthlete.speedData.shuttleRun === null
        ) {
          cleanedAthlete.speedData.shuttleRun = null;
        } else if (
          cleanedAthlete.speedData.shuttleRun &&
          !isNaN(parseFloat(cleanedAthlete.speedData.shuttleRun))
        ) {
          cleanedAthlete.speedData.shuttleRun = parseFloat(
            cleanedAthlete.speedData.shuttleRun
          );
        } else {
          cleanedAthlete.speedData.shuttleRun = null;
        }
      }

      // Debug logging after cleaning
      console.log("SpeedData after cleaning:", cleanedAthlete.speedData);

      // Handle standingReach and benchPress - these are now direct fields in the model
      if (
        cleanedAthlete.standingReach === "N/A" ||
        cleanedAthlete.standingReach === "" ||
        cleanedAthlete.standingReach === null
      ) {
        cleanedAthlete.standingReach = null;
      } else if (
        cleanedAthlete.standingReach &&
        !isNaN(parseFloat(cleanedAthlete.standingReach))
      ) {
        cleanedAthlete.standingReach = parseFloat(cleanedAthlete.standingReach);
      } else {
        cleanedAthlete.standingReach = null;
      }

      if (
        cleanedAthlete.benchPress === "N/A" ||
        cleanedAthlete.benchPress === "" ||
        cleanedAthlete.benchPress === null
      ) {
        cleanedAthlete.benchPress = null;
      } else if (
        cleanedAthlete.benchPress &&
        !isNaN(parseFloat(cleanedAthlete.benchPress))
      ) {
        cleanedAthlete.benchPress = parseFloat(cleanedAthlete.benchPress);
      } else {
        cleanedAthlete.benchPress = null;
      }

      // Clean graduation year specifically
      if (
        cleanedAthlete.graduationYear === "N/A" ||
        cleanedAthlete.graduationYear === "" ||
        cleanedAthlete.graduationYear === null
      ) {
        cleanedAthlete.graduationYear = null;
      } else if (
        cleanedAthlete.graduationYear &&
        !isNaN(cleanedAthlete.graduationYear)
      ) {
        cleanedAthlete.graduationYear = parseInt(cleanedAthlete.graduationYear);
      }

      // Handle dateOfBirth format
      if (cleanedAthlete.dateOfBirth) {
        if (
          cleanedAthlete.dateOfBirth === "N/A" ||
          cleanedAthlete.dateOfBirth === ""
        ) {
          cleanedAthlete.dateOfBirth = null;
        } else {
          // Try to parse the date correctly for MongoDB
          try {
            // Check if it's in DD/MM/YYYY format
            if (cleanedAthlete.dateOfBirth.includes("/")) {
              const [day, month, year] = cleanedAthlete.dateOfBirth.split("/");
              // Create date in YYYY-MM-DD format
              cleanedAthlete.dateOfBirth = new Date(`${year}-${month}-${day}`);
            } else {
              // Try standard date parsing
              cleanedAthlete.dateOfBirth = new Date(cleanedAthlete.dateOfBirth);
            }

            // If date is invalid, set to null
            if (isNaN(cleanedAthlete.dateOfBirth.getTime())) {
              cleanedAthlete.dateOfBirth = null;
            }
          } catch (e) {
            console.error("Error parsing date:", e);
            cleanedAthlete.dateOfBirth = null;
          }
        }
      }

      // Clean other fields that might have "N/A"
      const stringFields = ["position", "highSchool", "phone", "email"];
      stringFields.forEach((field) => {
        if (
          cleanedAthlete[field] === "N/A" ||
          cleanedAthlete[field] === "" ||
          cleanedAthlete[field] === null
        ) {
          cleanedAthlete[field] = null;
        }
      });

      // Clean address fields
      if (cleanedAthlete.address) {
        Object.keys(cleanedAthlete.address).forEach((key) => {
          if (
            cleanedAthlete.address[key] === "N/A" ||
            cleanedAthlete.address[key] === "" ||
            cleanedAthlete.address[key] === null
          ) {
            cleanedAthlete.address[key] = null;
          }
        });
      }

      if (cleanedAthlete.socialMedia) {
        Object.keys(cleanedAthlete.socialMedia).forEach((key) => {
          if (
            cleanedAthlete.socialMedia[key] === "N/A" ||
            cleanedAthlete.socialMedia[key] === "" ||
            cleanedAthlete.socialMedia[key] === null
          ) {
            cleanedAthlete.socialMedia[key] = null;
          }
          if (key === "hudl" || key === "youtube") {
            if (Array.isArray(cleanedAthlete.socialMedia[key])) {
              cleanedAthlete.socialMedia[key] = cleanedAthlete.socialMedia[
                key
              ].filter((link) => link && link !== "" && link !== "N/A");
            }
          }
        });
      }

      // Remove any remaining "N/A" values from the entire object recursively
      const cleanObject = (obj) => {
        for (let key in obj) {
          if (obj[key] === "N/A") {
            obj[key] = null;
          } else if (typeof obj[key] === "object" && obj[key] !== null) {
            cleanObject(obj[key]);
          }
        }
      };
      cleanObject(cleanedAthlete);

      // Ensure verification data is included in the save payload
      if (
        editedAthlete.verification &&
        Object.keys(editedAthlete.verification).length > 0
      ) {
        cleanedAthlete.verification = editedAthlete.verification;
      }

      onUpdateAthlete(cleanedAthlete);
    }
    setIsEditing(false);
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    console.log(
      `Updating field: ${field} with value: ${value}, isEditing: ${isEditing}`
    );

    // Handle speedData fields specially
    if (field === "fortyYardDash" || field === "shuttleRun") {
      updateSpeedDataField(field, value);
      return;
    }

    // Check if this is a nested field (contains a dot)
    if (field.includes(".")) {
      const [parentField, childField] = field.split(".");

      setEditedAthlete((prev) => {
        // Make sure the parent object exists
        const parentObj = prev[parentField] || {};

        return {
          ...prev,
          [parentField]: {
            ...parentObj,
            [childField]: value,
          },
        };
      });
    } else {
      // Handle regular fields
      setEditedAthlete((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // Handle adding a new transcript attachment
  const handleAddAttachment = () => {
    setEditedAthlete((prev) => ({
      ...prev,
      academics: {
        ...prev.academics,
        transcripts: [
          ...(Array.isArray(prev.academics?.transcripts)
            ? prev.academics.transcripts
            : []),
          { name: "", type: "pdf", url: "" },
        ],
      },
    }));
  };

  // Handle removing a transcript attachment
  const handleRemoveAttachment = (index) => {
    setEditedAthlete((prev) => ({
      ...prev,
      academics: {
        ...prev.academics,
        transcripts: (Array.isArray(prev.academics?.transcripts)
          ? prev.academics.transcripts
          : []
        ).filter((_, i) => i !== index),
      },
    }));
  };

  // Handle updating a specific attachment property
  const handleAttachmentChange = (index, property, value) => {
    setEditedAthlete((prev) => ({
      ...prev,
      academics: {
        ...prev.academics,
        transcripts: (Array.isArray(prev.academics?.transcripts)
          ? prev.academics.transcripts
          : []
        ).map((attachment, i) =>
          i === index ? { ...attachment, [property]: value } : attachment
        ),
      },
    }));
  };

  // Helper function to update speedData fields
  const updateSpeedDataField = (field, value) => {
    setEditedAthlete((prev) => {
      const speedData = { ...prev.speedData } || {};

      if (field === "fortyYardDash") {
        speedData.fortyYard = value;
      } else if (field === "shuttleRun") {
        speedData.shuttleRun = value;
      }

      return {
        ...prev,
        speedData,
      };
    });
  };

  // Handle adding a new link to a social media field
  const handleAddLink = (field) => {
    const newLink = "";

    // Check if this is a nested field (contains a dot)
    if (field.includes(".")) {
      const [parentField, childField] = field.split(".");

      setEditedAthlete((prev) => ({
        ...prev,
        [parentField]: {
          ...prev[parentField],
          [childField]: [...(prev[parentField]?.[childField] || []), newLink],
        },
      }));
    } else {
      setEditedAthlete((prev) => ({
        ...prev,
        [field]: [...(prev[field] || []), newLink],
      }));
    }
  };

  // Handle removing a link from a social media field
  const handleRemoveLink = (field, index) => {
    // Check if this is a nested field (contains a dot)
    if (field.includes(".")) {
      const [parentField, childField] = field.split(".");

      setEditedAthlete((prev) => ({
        ...prev,
        [parentField]: {
          ...prev[parentField],
          [childField]: (Array.isArray(prev[parentField]?.[childField])
            ? prev[parentField][childField]
            : []
          ).filter((_, i) => i !== index),
        },
      }));
    } else {
      setEditedAthlete((prev) => ({
        ...prev,
        [field]: (Array.isArray(prev[field]) ? prev[field] : []).filter(
          (_, i) => i !== index
        ),
      }));
    }
  };

  // Handle changing a link in a social media field
  const handleLinkChange = (field, index, value) => {
    // Check if this is a nested field (contains a dot)
    if (field.includes(".")) {
      const [parentField, childField] = field.split(".");

      setEditedAthlete((prev) => ({
        ...prev,
        [parentField]: {
          ...prev[parentField],
          [childField]: (Array.isArray(prev[parentField]?.[childField])
            ? prev[parentField][childField]
            : []
          ).map((link, i) => (i === index ? value : link)),
        },
      }));
    } else {
      setEditedAthlete((prev) => ({
        ...prev,
        [field]: (Array.isArray(prev[field]) ? prev[field] : []).map(
          (link, i) => (i === index ? value : link)
        ),
      }));
    }
  };

  // Render multiple links (HUDL, YouTube)
  const renderLinks = (label, links, field) => {
    const isMissingData =
      !links ||
      links.length === 0 ||
      (links.length === 1 &&
        (!links[0] || links[0] === "N/A" || links[0] === ""));

    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            color={isMissingData ? "error.main" : "text.secondary"}
            gutterBottom
          >
            {label}
            {isMissingData && " (Missing)"}
          </Typography>

          {isEditing ? (
            <Box>
              {field.includes("hudl") && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Add HUDL highlight links
                </Typography>
              )}
              {field.includes("youtube") && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Add YouTube video links
                </Typography>
              )}

              {(links || []).map((link, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <TextField
                    value={link || ""}
                    onChange={(e) =>
                      handleLinkChange(field, index, e.target.value)
                    }
                    size="small"
                    fullWidth
                    placeholder={`Enter ${
                      field.includes("hudl") ? "HUDL" : "YouTube"
                    } link`}
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveLink(field, index)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => handleAddLink(field)}
                sx={{ mt: 1 }}
              >
                Add {field.includes("hudl") ? "HUDL" : "YouTube"} Link
              </Button>
            </Box>
          ) : (
            <Box>
              {isMissingData ? (
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  sx={{ color: "error.main" }}
                >
                  N/A
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {(links || []).map(
                    (link, index) =>
                      link &&
                      link !== "N/A" && (
                        <Link
                          key={index}
                          href={
                            link.startsWith("http") ? link : `https://${link}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          {field.includes("hudl") ? (
                            <SportsSoccerIcon fontSize="small" sx={{ mr: 1 }} />
                          ) : (
                            <YouTubeIcon
                              fontSize="small"
                              sx={{ mr: 1 }}
                              color="error"
                            />
                          )}
                          {link.length > 30
                            ? `${link.substring(0, 30)}...`
                            : link}
                        </Link>
                      )
                  )}
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Render transcript attachments
  const renderAttachments = (label, attachments, field) => {
    const isMissingData = !attachments || attachments.length === 0;

    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            color={isMissingData ? "error.main" : "text.secondary"}
            gutterBottom
          >
            {label}
            {isMissingData && " (Missing)"}
          </Typography>

          {isEditing ? (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Add transcript documents
              </Typography>

              {(attachments || []).map((attachment, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <TextField
                    value={attachment.name || ""}
                    onChange={(e) =>
                      handleAttachmentChange(index, "name", e.target.value)
                    }
                    size="small"
                    placeholder="Document name"
                    sx={{ mr: 1, flexGrow: 1 }}
                  />
                  <TextField
                    select
                    value={attachment.type || "pdf"}
                    onChange={(e) =>
                      handleAttachmentChange(index, "type", e.target.value)
                    }
                    size="small"
                    sx={{ width: "100px", mr: 1 }}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="pdf">PDF</option>
                    <option value="doc">Word</option>
                  </TextField>
                  <TextField
                    value={attachment.url || ""}
                    onChange={(e) =>
                      handleAttachmentChange(index, "url", e.target.value)
                    }
                    size="small"
                    placeholder="URL to document"
                    sx={{ mr: 1, flexGrow: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveAttachment(index)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              <Button
                startIcon={<AttachFileIcon />}
                size="small"
                onClick={handleAddAttachment}
                sx={{ mt: 1 }}
              >
                Add Transcript
              </Button>
            </Box>
          ) : (
            <Box>
              {isMissingData ? (
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  sx={{ color: "error.main" }}
                >
                  N/A
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {(attachments || []).map(
                    (attachment, index) =>
                      attachment &&
                      attachment.name && (
                        <Link
                          key={index}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          {attachment.type === "pdf" ? (
                            <PdfIcon
                              fontSize="small"
                              sx={{ mr: 1 }}
                              color="error"
                            />
                          ) : (
                            <WordIcon
                              fontSize="small"
                              sx={{ mr: 1 }}
                              color="primary"
                            />
                          )}
                          {attachment.name}
                        </Link>
                      )
                  )}
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Render a metric with percentile (icons removed)
  // Handle toggling verification status
  const handleVerificationChange = (field, status) => {
    const now = new Date();
    const user =
      currentUser?.firstName && currentUser?.lastName
        ? `${currentUser.firstName} ${currentUser.lastName}`
        : currentUser?.name || "Coach";

    setEditedAthlete((prev) => ({
      ...prev,
      verification: {
        ...(prev.verification || {}),
        [field]: {
          status: status, // 'E' for estimated, 'V' for verified
          updatedBy: user,
          timestamp: now.toISOString(),
        },
      },
    }));
  };

  const renderMetric = (
    icon, // kept as parameter for backward compatibility
    label,
    value,
    percentile,
    unit = "",
    field = null
  ) => {
    // Check if the value is missing or N/A
    const isMissingData =
      value === undefined ||
      value === null ||
      value === "N/A" ||
      value === "undefined" ||
      value === "" ||
      (typeof value === "string" && value.trim() === "") ||
      (typeof value === "string" && value.includes("N/A"));

    // Define which fields should show verification (only physical measurements and performance metrics)
    const verificationFields = [
      "height",
      "weight",
      "wingspan",
      "standingReach",
      "verticalJump",
      "benchPress",
      "fortyYardDash",
      "shuttleRun",
    ];
    const showVerification = field && verificationFields.includes(field);

    // Get verification status for this field (only for verification fields)
    const verification =
      showVerification &&
      (isEditing
        ? editedAthlete.verification?.[field]
        : athlete.verification?.[field]);

    // Format timestamp for tooltip
    const formatTimestamp = (timestamp) => {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      return date.toLocaleString();
    };

    // Get tooltip text for verification status
    const getVerificationTooltip = (verification) => {
      if (!verification) return "Not verified";
      const status = verification.status === "V" ? "Verified" : "Estimated";
      return `${status} by ${verification.updatedBy} on ${formatTimestamp(
        verification.timestamp
      )}`;
    };

    return (
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            color={isMissingData ? "error.main" : "text.secondary"}
            gutterBottom
          >
            {label}
            {isMissingData && " (Missing)"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isEditing && field ? (
              <>
                <TextField
                  value={
                    field.includes(".")
                      ? editedAthlete[field.split(".")[0]]?.[
                          field.split(".")[1]
                        ] || ""
                      : field === "fortyYardDash"
                      ? editedAthlete.speedData?.fortyYard || ""
                      : field === "shuttleRun"
                      ? editedAthlete.speedData?.shuttleRun || ""
                      : editedAthlete[field] || ""
                  }
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 120, mr: 1 }}
                  error={isMissingData}
                  helperText={isMissingData ? "Required" : ""}
                  InputProps={{
                    endAdornment: unit ? (
                      <Typography variant="body2">{unit}</Typography>
                    ) : null,
                  }}
                />
                {/* Verification toggle buttons - only for physical measurements and performance metrics */}
                {showVerification && (
                  <ButtonGroup size="small" sx={{ ml: 1 }}>
                    <Tooltip title="Mark as Estimated">
                      <Button
                        onClick={() => handleVerificationChange(field, "E")}
                        variant={
                          verification?.status === "E"
                            ? "contained"
                            : "outlined"
                        }
                        sx={{
                          bgcolor:
                            verification?.status === "E"
                              ? "warning.light"
                              : "inherit",
                          color:
                            verification?.status === "E"
                              ? "warning.contrastText"
                              : "warning.main",
                          "&:hover": {
                            bgcolor:
                              verification?.status === "E"
                                ? "warning.main"
                                : "warning.light",
                          },
                        }}
                      >
                        E
                      </Button>
                    </Tooltip>
                    <Tooltip title="Mark as Verified">
                      <Button
                        onClick={() => handleVerificationChange(field, "V")}
                        variant={
                          verification?.status === "V"
                            ? "contained"
                            : "outlined"
                        }
                        sx={{
                          bgcolor:
                            verification?.status === "V"
                              ? "info.light"
                              : "inherit",
                          color:
                            verification?.status === "V"
                              ? "info.contrastText"
                              : "info.main",
                          "&:hover": {
                            bgcolor:
                              verification?.status === "V"
                                ? "info.main"
                                : "info.light",
                          },
                        }}
                      >
                        V
                      </Button>
                    </Tooltip>
                  </ButtonGroup>
                )}
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  sx={{ color: isMissingData ? "error.main" : "inherit" }}
                >
                  {isMissingData ? "Not set" : `${value} ${unit}`}
                </Typography>

                {/* Show verification badge if available - only for physical measurements and performance metrics */}
                {showVerification && verification && (
                  <Tooltip title={getVerificationTooltip(verification)}>
                    <Box
                      sx={{
                        ml: 1,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor:
                          verification.status === "V"
                            ? "info.light"
                            : "warning.light",
                        color:
                          verification.status === "V"
                            ? "info.contrastText"
                            : "warning.contrastText",
                        fontSize: "0.7rem",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 20,
                        height: 20,
                      }}
                    >
                      {verification.status}
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}
            {percentile && (
              <Tooltip
                title={`${percentile}th percentile among ${athlete.position}s`}
              >
                <Box
                  sx={{
                    ml: 1,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: getPercentileColor(percentile),
                    color: "white",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                  }}
                >
                  {percentile}%
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Athlete Vitals
        </Typography>
        <Box>
          {isEditing ? (
            <>
              <IconButton
                size="small"
                onClick={handleSave}
                color="primary"
                sx={{ mr: 1 }}
                title="Save changes"
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleEditToggle}
                color="secondary"
                title="Cancel editing"
              >
                <CancelIcon />
              </IconButton>
            </>
          ) : (
            <IconButton
              size="small"
              onClick={handleEditToggle}
              color="primary"
              title="Edit vitals"
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {/* Physical Measurements */}
        <Grid item xs={12} md={6}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: "medium" }}
          >
            Physical Measurements
          </Typography>

          {renderMetric(
            <HeightIcon color="primary" />,
            "Height",
            isEditing ? editedAthlete.height : athlete.height,
            athlete.percentiles?.height,
            "",
            "height"
          )}

          {renderMetric(
            <FitnessCenterIcon color="primary" />,
            "Weight",
            isEditing ? editedAthlete.weight : athlete.weight,
            athlete.percentiles?.weight,
            "lbs",
            "weight"
          )}

          {renderMetric(
            <StraightenIcon color="primary" />,
            "Wingspan",
            isEditing ? editedAthlete.wingspan : athlete.wingspan,
            athlete.percentiles?.wingspan,
            "in",
            "wingspan"
          )}

          {renderMetric(
            <StraightenIcon color="primary" />,
            "Standing Reach",
            isEditing ? editedAthlete.standingReach : athlete.standingReach,
            athlete.percentiles?.standingReach,
            "in",
            "standingReach"
          )}
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12} md={6}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: "medium" }}
          >
            Performance Metrics
          </Typography>

          {renderMetric(
            null,
            "40-Yard Dash",
            isEditing
              ? editedAthlete.speedData?.fortyYard ||
                  editedAthlete.fortyYardDash
              : athlete.speedData?.fortyYard || athlete.fortyYardDash,
            athlete.percentiles?.fortyYardDash,
            "s",
            "fortyYardDash"
          )}

          {renderMetric(
            null,
            "Vertical Jump",
            isEditing ? editedAthlete.verticalJump : athlete.verticalJump,
            athlete.percentiles?.verticalJump,
            "in",
            "verticalJump"
          )}

          {renderMetric(
            null,
            "Bench Press",
            isEditing ? editedAthlete.benchPress : athlete.benchPress,
            athlete.percentiles?.benchPress,
            "lbs",
            "benchPress"
          )}

          {renderMetric(
            null,
            "Shuttle Run",
            isEditing
              ? editedAthlete.speedData?.shuttleRun || editedAthlete.shuttleRun
              : athlete.speedData?.shuttleRun || athlete.shuttleRun,
            athlete.percentiles?.shuttleRun,
            "s",
            "shuttleRun"
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Prospect Information */}
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{ fontWeight: "medium" }}
      >
        Prospect Information
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          {renderMetric(
            null,
            "Position",
            isEditing ? editedAthlete.position : athlete.position || null,
            null,
            "",
            "position"
          )}

          {renderMetric(
            null,
            "Class Year",
            isEditing
              ? editedAthlete.graduationYear
              : athlete.graduationYear || null,
            null,
            "",
            "graduationYear"
          )}

          {renderMetric(
            null,
            "High School",
            isEditing ? editedAthlete.highSchool : athlete.highSchool || null,
            null,
            "",
            "highSchool"
          )}

          {isEditing ? (
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Date of Birth
                </Typography>
                <TextField
                  type="date"
                  value={
                    editedAthlete.dateOfBirth
                      ? (() => {
                          try {
                            const date = new Date(editedAthlete.dateOfBirth);
                            if (isNaN(date.getTime())) return "";
                            return date.toISOString().split("T")[0];
                          } catch (e) {
                            console.error("Error parsing date:", e);
                            return "";
                          }
                        })()
                      : ""
                  }
                  onChange={(e) =>
                    handleInputChange("dateOfBirth", e.target.value)
                  }
                  size="small"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 120, mr: 1 }}
                />
              </Box>
            </Box>
          ) : (
            renderMetric(
              null,
              "Age / DOB",
              athlete.dateOfBirth ? formatDate(athlete.dateOfBirth) : null,
              null,
              "",
              null
            )
          )}

          {renderMetric(
            null,
            "Phone",
            isEditing ? editedAthlete.phone : athlete.phone || null,
            null,
            "",
            "phone"
          )}

          {renderMetric(
            null,
            "Email",
            isEditing ? editedAthlete.email : athlete.email || null,
            null,
            "",
            "email"
          )}

          {/* Address Fields */}
          {renderMetric(
            null,
            "Street Address",
            isEditing
              ? editedAthlete.address?.street
              : athlete.address?.street || null,
            null,
            "",
            "address.street"
          )}

          {renderMetric(
            null,
            "City",
            isEditing
              ? editedAthlete.address?.city
              : athlete.address?.city || null,
            null,
            "",
            "address.city"
          )}

          {renderMetric(
            null,
            "State",
            isEditing
              ? editedAthlete.address?.state
              : athlete.address?.state || null,
            null,
            "",
            "address.state"
          )}

          {renderMetric(
            null,
            "ZIP Code",
            isEditing
              ? editedAthlete.address?.zipCode
              : athlete.address?.zipCode || null,
            null,
            "",
            "address.zipCode"
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {renderMetric(
            null,
            "X Account",
            isEditing
              ? editedAthlete.socialMedia?.twitter
              : athlete.socialMedia?.twitter || null,
            null,
            "",
            "socialMedia.twitter"
          )}

          {renderMetric(
            null,
            "Instagram",
            isEditing
              ? editedAthlete.socialMedia?.instagram
              : athlete.socialMedia?.instagram || null,
            null,
            "",
            "socialMedia.instagram"
          )}

          {renderLinks(
            "HUDL",
            isEditing
              ? editedAthlete.socialMedia?.hudl
              : athlete.socialMedia?.hudl || [],
            "socialMedia.hudl"
          )}

          {renderLinks(
            "YouTube",
            isEditing
              ? editedAthlete.socialMedia?.youtube
              : athlete.socialMedia?.youtube || [],
            "socialMedia.youtube"
          )}

          {renderAttachments(
            "Transcripts",
            isEditing
              ? editedAthlete.academics?.transcripts
              : athlete.academics?.transcripts || [],
            "academics.transcripts"
          )}

          {/* Evaluations - Navigate to Notes */}
          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Evaluations
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  sx={{ flex: 1 }}
                >
                  {athlete.evaluations && athlete.evaluations.length > 0
                    ? `${athlete.evaluations.length} evaluation(s)`
                    : "No evaluations yet"}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onNavigateToNotes}
                  startIcon={<AddIcon />}
                >
                  Open in Notes
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Injury History - Navigate to Notes */}
          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Injury History
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  sx={{ flex: 1 }}
                >
                  {athlete.injuryHistory && athlete.injuryHistory.length > 0
                    ? `${athlete.injuryHistory.length} injury record(s)`
                    : "No injury records yet"}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onNavigateToNotes}
                  startIcon={<AddIcon />}
                >
                  Open in Notes
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Additional Information */}
      {athlete.additionalInfo && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: "medium" }}
          >
            Additional Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {athlete.additionalInfo}
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default AthleteVitals;
